<template>
  <SampleOrderHint v-if="canOrderSamples" :source="source">
    <button class="btn btn-primary icon-btn" @click="orderSamples">
      <Icon icon="shipping-box" />
      {{ $t('SAMPLE_ORDER.ENTRY_POINT') }}
    </button>
  </SampleOrderHint>
</template>

<script setup lang="ts">
/**
 * The sample order entry point on a composition page: the button, when to show it, and the call into
 * the journey. It takes the composition from the store, which is why it lives here and not next to
 * the journey — and it renders nothing when that composition cannot be sampled, so callers do not
 * repeat the condition. See `docs/publishing/sample-order-journey.md`.
 *
 * The first-time hint wraps the button, so both placements get it from this one component.
 *
 * Two conditions: the staged rollout, and the rule about this composition. The only entry point
 * standing in a scope already, so the only one applying the rollout's scope half itself. The mode is
 * not one of them — see `hasSampleableProducts`; it is still tracked, so UNSUPPORTED can be counted.
 */
import { computed } from 'vue';
import { useCompositionStore } from 'commons/stores/composition';
import { usePublishingStore } from 'commons/stores/publishing';
import { sampleOrderAvailableInScope } from 'commons/sampleOrder/sampleOrderAvailability';
import { hasSampleableProducts } from 'commons/sampleOrder/sampleOrderEligibility';
import { openSampleOrderJourney } from 'commons/sampleOrder/sampleOrderJourney';
import {
  trackSampleOrderEntryClick,
  type SampleOrderSource,
} from 'commons/sampleOrder/sampleOrderTracking';
import SampleOrderHint from './SampleOrderHint.vue';

const props = defineProps<{ source: SampleOrderSource }>();

const compositionStore = useCompositionStore();
const publishingStore = usePublishingStore();

const switchedOnHere = () =>
  sampleOrderAvailableInScope(publishingStore.currentScope?.key);

const canOrderSamples = computed(
  () => switchedOnHere() && hasSampleableProducts(compositionStore.current),
);

// Repeats the condition rather than reading `canOrderSamples`, so `current` and the scope narrow.
const orderSamples = () => {
  const scopeKey = publishingStore.currentScope?.key;
  if (
    !scopeKey ||
    !switchedOnHere() ||
    !hasSampleableProducts(compositionStore.current)
  ) {
    return;
  }

  trackSampleOrderEntryClick({
    source: props.source,
    scope: scopeKey,
    mode: compositionStore.mode,
  });

  openSampleOrderJourney(compositionStore.current, {
    scope: scopeKey,
    source: props.source,
  }).catch(() => {
    // cancelled without ordering — nothing to do
  });
};
</script>

<style lang="scss" scoped>
// Height comes from `.btn.icon-btn`; only the width is local, so it does not stretch to its column.
button {
  width: fit-content;
}
</style>
